import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Ban,
  BookOpen,
  Copy,
  Eye,
  FileText,
  PenLine,
  RotateCw,
  Send,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import type {
  PrequalBuyerDocumentPath,
  PrequalInternalVerification,
  PrequalInviteDto,
  PrequalInviteStatus,
  PrequalificationDraftScope,
  PrequalificationDraftSummaryDto,
  PrequalificationOutcome,
  PrequalificationRole,
  PrequalificationStatsDto,
  PrequalificationStatus,
  PrequalificationSummaryDto,
} from "@/api/prequalification";
import {
  usePartnerBrandOptions,
  usePrequalificationDrafts,
  usePrequalificationInvites,
  usePrequalificationList,
  usePrequalificationStats,
  useResendPrequalificationInvite,
  useRevokePrequalificationInvite,
} from "@/hooks/prequalification/usePrequalification";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { useAuth } from "@/hooks/useAuth";
import { Badge, Button, IconBtn, Input, Tabs, Tooltip } from "@/design-system/components/ui";
import {
  PREQUAL_INVITE_FILTER_STATUSES,
  PREQUAL_INVITE_STATUS_BADGE,
  isTerminalInviteStatus,
  terminalInviteReason,
} from "@/design-system/lib/prequalInviteStatus";
import { formatCurrency } from "@/design-system/lib/format";
import { PREQUAL_PROGRAM_TERMS } from "@/portal/pages/prequalify/prequalSizing";
import { cn } from "@/design-system/lib/utils";
import { SendPrequalInviteModal } from "@/components/prequalification/SendPrequalInviteModal";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useDateFormatter } from "@/utils/datetime";

const PAGE_SIZE = 25;

/** IconBtn's own look, worn by the two header actions that are navigations (see
 *  the comment at their call site for why those are anchors and not buttons).
 *  Shared so the four actions in that row cannot drift apart visually. */
const HEADER_ACTION_LINK_CLASS = cn(
  "inline-flex h-9 w-9 items-center justify-center",
  "bg-transparent border border-border-light rounded-md text-onyx",
  "transition-colors duration-fast ease-standard",
  "hover:bg-white-warm",
  "focus-visible:outline-none focus-visible:shadow-goldRing",
);

const EMPTY_STATS: PrequalificationStatsDto = {
  total: 0,
  byStatus: { New: 0, Reviewed: 0, Converted: 0, Disqualified: 0 },
  passCount: 0,
  softDeclineCount: 0,
};

export const PREQUAL_STATUS_BADGE: Record<
  PrequalificationStatus,
  "draft" | "info" | "gold" | "finalized" | "archived" | "review" | "error"
> = {
  New: "draft",
  Reviewed: "info",
  Converted: "finalized",
  Disqualified: "error",
};

export const PREQUAL_OUTCOME_BADGE: Record<PrequalificationOutcome, "gold" | "review"> = {
  Pass: "gold",
  SoftDecline: "review",
};

export const outcomeLabel = (o: PrequalificationOutcome) =>
  o === "SoftDecline" ? "Soft decline" : "Pass";

/**
 * The relationship-verification badge for one row, or `null` where there is no
 * relationship to have verified.
 *
 * 🔴 **THE GUARANTEE IS STRUCTURAL, NOT A RULE EVERY CALL SITE REMEMBERS.**
 * `internalRelationshipVerification` is NOT NULL on every submission and
 * defaults to `Unverified`, so every seller and every capital-sized buyer sits
 * there for life — a badge keyed on that value alone would put an unverified
 * marker on almost every row in the book. This takes the ROW rather than the
 * two fields, so a caller cannot obtain a badge for a non-internal submission
 * and cannot transpose the arguments; the only way to render one wrongly is to
 * stop calling this. (The `DeclineLetterModel` precedent: make the wrong
 * output unreachable rather than gated on a branch.)
 *
 * 🔴 **IT GATES NOTHING (D6).** No document, no email and no later step reads
 * this value, and the red tone says "a person looked and could not confirm
 * it", never "the system is blocking on it". `Disqualified` on this page's own
 * status map is red for the same reason — a definitive negative conclusion,
 * not an overdue item.
 *
 * ⚠️ **"Unchecked" and "Not verified" are DIFFERENT STATES and the labels are
 * chosen so they cannot be read as one.** Unchecked = nobody has looked yet
 * (the default, and not a problem); Not verified = a staff member looked and it
 * did not hold. Collapsing them into the near-homographs the enum members use
 * ("Unverified" / "NotVerified") is the edit to refuse.
 */
export function internalVerificationBadge(row: {
  buyerDocumentPath?: PrequalBuyerDocumentPath | null;
  internalRelationshipVerification: PrequalInternalVerification;
}): { status: "finalized" | "error" | "archived"; label: string } | null {
  if (row.buyerDocumentPath !== "InternalBuyer") return null;
  switch (row.internalRelationshipVerification) {
    case "Verified":
      return { status: "finalized", label: "Verified" };
    case "NotVerified":
      return { status: "error", label: "Not verified" };
    default:
      return { status: "archived", label: "Unchecked" };
  }
}

const STATUSES: PrequalificationStatus[] = ["New", "Reviewed", "Converted", "Disqualified"];

/**
 * The site filter's Phoenix-native option.
 *
 * A DISTINCT sentinel, not `""`: the empty value already means "no site
 * filter", and the server's `partnerBrandId` is a `Guid?` where null likewise
 * means unfiltered — so "no filter" and "the native funnel only" are not
 * expressible in one field. This maps to the separate `nativeOnly` flag.
 * Brand values are GUIDs, so the literal can never collide with one.
 */
const NATIVE_SITE = "native";

/**
 * The three lists this page owns, as PEER tabs.
 *
 * Completions used to be the permanent page body with the other two stacked
 * underneath it, which put three paginators on one screen reading as one
 * continuous list and made the submissions table (plus its counters) a cost
 * every visitor paid regardless of what they came for. All three are the same
 * kind of thing — a list about one prequalification funnel — so all three are
 * tabs, and only the selected one mounts. This matches the partner portal and
 * the broker/brokerage record sub-tabs, which already render exactly these
 * three behind the same primitive.
 *
 * Deliberately NO count badges (decision D8): every one of these sections is a
 * plain `useQuery` with no `enabled` guard, so mounting a hidden one to source
 * a count costs an extra list read on first paint; and the counts are
 * filter-dependent (invites default to `mineOnly`, drafts to `Abandoned`,
 * completions to the current filter row), so a number on a tab nobody has
 * opened would describe a view they are not looking at.
 */
type WorkQueueTab = "completions" | "invitations" | "in-progress";

/**
 * The tab strip, and the ONLY list of recognised `?tab=` values — the parser
 * below reads its ids, so the two can never drift.
 *
 * 🔴 `invitations` is a LIVE BACKEND DEEP LINK: `PrequalInviteNudgeWorker`
 * emits `/deal-flow/prequalification?tab=invitations` on every nudge. Adding
 * `completions` alongside it keeps that URL working verbatim — the slug must
 * not be renamed here without changing the worker.
 */
const WORK_QUEUE_TABS: { id: WorkQueueTab; label: string }[] = [
  { id: "completions", label: "Completions" },
  { id: "invitations", label: "Invitations" },
  { id: "in-progress", label: "In progress" },
];

/**
 * Absent or unrecognised → completions, SILENTLY. A bad `?tab=` is a typo, not
 * a failure, and completions is the list somebody arriving with no opinion
 * wants. (Same rule as the record-page sub-tabs' `?view=`.)
 */
function parseWorkQueueTab(v: string | null): WorkQueueTab {
  return WORK_QUEUE_TABS.find((t) => t.id === v)?.id ?? "completions";
}

/** Whether a stamp is `prequal-v7` or later — the engine that sizes the seller
 *  and the internal buyer on both payment schedules (1 November 2026 terms). */
export function isScheduleEngine(engineVersion: string | null | undefined): boolean {
  const m = /^prequal-v(\d+)$/.exec(engineVersion ?? "");
  return m != null && Number(m[1]) >= 7;
}

/** The staff list's "Qualified / Financed" cell (M42, ruling Q9).
 *
 *  A prequal-v7 seller or internal-buyer pass states the MAXIMUM FINANCED AMOUNT
 *  on each schedule, 20-year first (the headline, D5) — "20-yr $500,000 · 10-yr
 *  $400,567" — and names the minimum instead of a figure when the 10-year
 *  schedule falls below it. A capital buyer keeps the qualified price it always
 *  showed. A row sized before v7 (no Maximum on the row) keeps the old cell:
 *  the buyer headline or the seller's supportable price range (D15). */
export function amountCell(s: {
  role: PrequalificationRole;
  qualifiedAmount?: number | null;
  supportablePriceLow?: number | null;
  supportablePriceHigh?: number | null;
  engineVersion?: string | null;
  buyerDocumentPath?: string | null;
  maxFinanced20?: number | null;
  maxFinanced10?: number | null;
  belowMinimum10?: boolean | null;
}): string {
  const onSchedules =
    isScheduleEngine(s.engineVersion) &&
    (s.role !== "Buyer" || s.buyerDocumentPath === "InternalBuyer");
  if (onSchedules && s.maxFinanced20 != null) {
    const tenYear = s.belowMinimum10
      ? `below ${formatCurrency(PREQUAL_PROGRAM_TERMS.minAmountFinanced)}`
      : s.maxFinanced10 != null
        ? formatCurrency(s.maxFinanced10)
        : "—";
    return `20-yr ${formatCurrency(s.maxFinanced20)} · 10-yr ${tenYear}`;
  }
  if (s.role === "Buyer") {
    return s.qualifiedAmount != null ? formatCurrency(s.qualifiedAmount) : "—";
  }
  if (s.supportablePriceLow != null && s.supportablePriceHigh != null) {
    return `${formatCurrency(s.supportablePriceLow)} – ${formatCurrency(s.supportablePriceHigh)}`;
  }
  return "—";
}

export function PrequalificationListPage() {
  // Set explicitly rather than left to URL derivation. Derivation reads the
  // path, and LABELS maps the "deal-flow" stem to "Deals" with no entry for
  // "prequalification" — so this page's trail rendered "Deals › prequalification",
  // a third parent for the surface and a raw lowercase segment. The nav group
  // is Partners; an explicit trail says so and supersedes derivation entirely,
  // which is why no ROUTE_PATTERNS entry is needed. The Partners crumb carries
  // no href: it is a sidebar GROUP, not a route.
  useBreadcrumbs([{ label: "Partners" }, { label: "Prequalification" }]);

  const { hasPermission } = useAuth();
  const canManageDocuments = hasPermission("Prequalification.ManageDocuments");
  const canManageInvites = hasPermission("Prequalification.Manage");
  const canReadBrands = hasPermission("Partners.ManageBrands");
  const navigate = useNavigate();

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inPersonModalOpen, setInPersonModalOpen] = useState(false);

  // 🔴 `?tab=` is the SINGLE SOURCE OF TRUTH — no mirrored useState, so a Back
  // press or a pasted deep link cannot disagree with what is painted.
  const [params, setParams] = useSearchParams();
  const workQueueTab = parseWorkQueueTab(params.get("tab"));
  // COPY the current params and touch one key, so anything else on the URL
  // survives the switch. `replace` because a tab is a view toggle, not
  // navigation: somebody three tabs deep should get Back out of the page, not
  // back through the strip. Nothing else on this page writes to the URL — the
  // filter row is local state — so this is the page's only history decision.
  const selectWorkQueueTab = (next: string) => {
    const merged = new URLSearchParams(params);
    merged.set("tab", next);
    setParams(merged, { replace: true });
  };

  return (
    <>
      <PageHeader
        // The nav group moved to Partners; the eyebrow was left behind saying
        // Deal Flow. The URL deliberately stays /deal-flow/prequalification —
        // backend notification deep links point at it.
        eyebrow="Partners"
        title="Prequalification"
        // Icon-only, so three labelled buttons stop crowding the title out of
        // its column. Each carries the SAME words it used to spell out, on
        // `aria-label` (the accessible name, so nothing is lost to a screen
        // reader or to a test) and on a Tooltip that opens on hover AND on
        // keyboard focus. The Tooltip clones its child rather than wrapping it,
        // so these are still three flex items, not three wrappers.
        actions={
          <>
            {canManageDocuments && (
              // The applicant-facing consent + legal fine print on the public
              // funnel. Same permission and same editor as "Edit document
              // language" beside it — one editor, two doors — but a distinct
              // door, because this copy is what a person ticks a box to accept
              // and finding it two clicks inside a tab called "Buyer letter" is
              // not findable. Deep-links straight to its tab; the key is a route
              // value, so it is written as a literal here rather than imported
              // from the backend registry.
              <Tooltip label="Edit form fine print">
                <Link
                  to="/deal-flow/prequalification/documents?doc=funnel-fine-print"
                  aria-label="Edit form fine print"
                  className={HEADER_ACTION_LINK_CLASS}
                >
                  <ShieldCheck size={16} strokeWidth={1.5} aria-hidden />
                </Link>
              </Tooltip>
            )}
            {canManageDocuments && (
              // An icon-sized LINK, deliberately not an IconBtn: IconBtn renders
              // a hard `<button>` and offers no `as`/`asChild`, so keeping the
              // Link around it would nest a button inside an anchor — invalid
              // HTML, and it breaks middle-click and open-in-new-tab on the two
              // header actions that are navigations. The anchor therefore stays
              // the interactive element and wears IconBtn's own classes so the
              // actions still read as one set.
              <Tooltip label="Edit document language">
                <Link
                  to="/deal-flow/prequalification/documents"
                  aria-label="Edit document language"
                  className={HEADER_ACTION_LINK_CLASS}
                >
                  <FileText size={16} strokeWidth={1.5} aria-hidden />
                </Link>
              </Tooltip>
            )}
            {canManageDocuments && (
              // The uploaded program PDF, beside the copy editor because they are
              // the two halves of the same job: this module's applicant-facing
              // language lives partly in editable blocks and partly in a file, and
              // both are gated on Prequalification.ManageDocuments. Without a door
              // here the page is only reachable by typing the URL.
              <Tooltip label="Manage program brochure">
                <Link
                  to="/deal-flow/prequalification/brochure"
                  aria-label="Manage program brochure"
                  className={HEADER_ACTION_LINK_CLASS}
                >
                  <BookOpen size={16} strokeWidth={1.5} aria-hidden />
                </Link>
              </Tooltip>
            )}
            {canManageInvites && (
              <>
                {/* Beside send-invite, because it is the same decision made a
                    different way: this person is in the room instead of on the
                    end of an email. Same permission, same site choice. */}
                <Tooltip label="Start in person">
                  <IconBtn
                    label="Start in person"
                    onClick={() => setInPersonModalOpen(true)}
                  >
                    <UserPlus size={16} strokeWidth={1.5} aria-hidden />
                  </IconBtn>
                </Tooltip>
                {/* Still the page's primary action. Shrinking it to an icon
                    should not also demote it, so the gold carries the emphasis
                    the filled Button used to. */}
                <Tooltip label="Send invitation">
                  <IconBtn
                    label="Send invitation"
                    onClick={() => setInviteModalOpen(true)}
                    className="bg-gold border-gold hover:bg-gold-highlight"
                  >
                    <Send size={16} strokeWidth={1.5} aria-hidden />
                  </IconBtn>
                </Tooltip>
              </>
            )}
          </>
        }
      />

      <SendPrequalInviteModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        canReadBrands={canReadBrands}
      />

      <SendPrequalInviteModal
        open={inPersonModalOpen}
        onClose={() => setInPersonModalOpen(false)}
        canReadBrands={canReadBrands}
        variant="in-person"
        // Straight into the form. The assist route is under /deal-flow so it
        // stays in the staff bundle (main.tsx sends anything starting with
        // /prequalify to the isolated portal one) and so the form's own
        // in-funnel links resolve against a path this app actually serves.
        onStarted={(draftId) => navigate(`/deal-flow/prequalify/assist/${draftId}`)}
      />

      <Tabs
        tabs={WORK_QUEUE_TABS}
        activeId={workQueueTab}
        onChange={selectWorkQueueTab}
        ariaLabel="Prequalification lists"
      >
        {/* Only the ACTIVE panel mounts. That is the whole point of the three
            sections being components rather than inlined JSX: nothing here is
            gated on which tab is showing, so a co-mounted panel would keep
            fetching for a tab nobody is looking at. The list reads carry no
            `enabled` at all, and the one that does — usePartnerBrandOptions,
            gated on Partners.ManageBrands — is guarded on a PERMISSION, which
            says nothing about the tab: for anyone holding it, it would fire
            just the same. Unmounting is what actually stops them, and it is
            also why the labels carry no counts (D8). */}
        {workQueueTab === "completions" && (
          <CompletionsSection canReadBrands={canReadBrands} />
        )}
        {workQueueTab === "invitations" && <InvitesSection canManage={canManageInvites} />}
        {workQueueTab === "in-progress" && <DraftsSection canAssist={canManageInvites} />}
      </Tabs>
    </>
  );
}

/**
 * The completed prequalifications: the counters, the filter row, the table.
 *
 * This was the page body, rendered above the tab strip on every visit. It is a
 * SECTION now — a real component, not JSX behind `workQueueTab === "completions"`
 * — because everything it needs is a plain `useQuery` with no `enabled` guard:
 * the list, the counters, the partner-brand options and the assignable users
 * all fire on mount. Left inlined at page level those four reads would still
 * run while somebody reads the invitations queue, so the invite nudge's
 * `?tab=invitations` deep link would go on paying for a submissions page
 * nobody asked for. As its own component they mount and unmount with the tab.
 *
 * The filter state lives here for the same reason, and stays LOCAL rather than
 * moving to the URL: `?tab=` is the page's only addressable state, and putting
 * a search box on the URL would push a history entry per keystroke.
 */
function CompletionsSection({ canReadBrands }: { canReadBrands: boolean }) {
  const { date } = useDateFormatter();

  const [search, setSearch] = useState("");
  // "Archived" is a sentinel — it drives the dedicated archived filter
  // rather than a status (the leads convention).
  const [status, setStatus] = useState<PrequalificationStatus | "" | "Archived">("");
  const [role, setRole] = useState<PrequalificationRole | "">("");
  const [outcome, setOutcome] = useState<PrequalificationOutcome | "">("");
  // "" = every site; NATIVE_SITE = the Phoenix-native funnel; anything else is
  // a brand id. Two intents, two server fields — see NATIVE_SITE.
  const [site, setSite] = useState("");
  // D11 — "show me the ones that should have documents and do not". The server
  // predicate is PER-OUTCOME: a Pass owes a letter and a deal sheet, a soft
  // decline owes a letter and an analysis. It is not Pass-only, and this comment
  // said it was right up until the decline set shipped — which would have left
  // the one filter built to find a failed generation blind to exactly the
  // outcome whose documents were newest. The caption below states the rule.
  const [documentsMissing, setDocumentsMissing] = useState(false);
  // D6 — how staff answered the relationship an internal buyer asserted. The
  // server ANDs the internal path onto this, because the column is NOT NULL on
  // every submission: without that clause "Unchecked" would return the whole
  // book. The optgroup below says so on screen, so an operator is never left
  // wondering why picking a value also hid every seller.
  const [internalVerification, setInternalVerification] =
    useState<PrequalInternalVerification | "">("");
  // L6 — "In Buyer CRM": Yes / No / Any. The server scopes "No" to buyer
  // completions (a seller is never sent there) and decides "linked" with the
  // same Z1 rule the column and the detail page's button read.
  const [inBuyerCrm, setInBuyerCrm] = useState<"" | "yes" | "no">("");
  const inBuyerCrmParam = inBuyerCrm === "" ? undefined : inBuyerCrm === "yes";
  const [page, setPage] = useState(1);

  const archivedView = status === "Archived";
  const statusParam: PrequalificationStatus | undefined =
    status === "" || status === "Archived" ? undefined : status;
  const nativeOnly = site === NATIVE_SITE;
  const partnerBrandId = nativeOnly ? undefined : site || undefined;

  useEffect(() => {
    setPage(1);
  }, [search, status, role, outcome, site, documentsMissing, internalVerification, inBuyerCrm]);

  const list = usePrequalificationList({
    page,
    pageSize: PAGE_SIZE,
    q: search || undefined,
    status: statusParam,
    role: role || undefined,
    outcome: outcome || undefined,
    archived: archivedView || undefined,
    partnerBrandId,
    nativeOnly: nativeOnly || undefined,
    documentsMissing: documentsMissing || undefined,
    internalVerification: internalVerification || undefined,
    inBuyerCrm: inBuyerCrmParam,
  });

  // Partner sites, for the submissions filter. Gated on Partners.ManageBrands
  // so a caller without it never fires a 403. (The invite modals live at page
  // level and fetch their own — they are reachable from every tab.)
  const brands = usePartnerBrandOptions(canReadBrands);
  const brandOptions = useMemo(
    () => (brands.data ?? []).filter((b) => b.isActive),
    [brands.data],
  );

  // Counters reflect the entire filtered set (status intentionally omitted;
  // archived always excluded server-side).
  const statsQuery = usePrequalificationStats({
    q: search || undefined,
    role: role || undefined,
    outcome: outcome || undefined,
    // List and stats share the server's ApplyFilters, so the site narrows
    // both — the counters can never drift above the filtered table.
    partnerBrandId,
    nativeOnly: nativeOnly || undefined,
    // Same reason as the site above: list and stats share the server's
    // ApplyFilters, so a counter can never read higher than the table under it.
    documentsMissing: documentsMissing || undefined,
    internalVerification: internalVerification || undefined,
    inBuyerCrm: inBuyerCrmParam,
  });
  const stats = statsQuery.data ?? EMPTY_STATS;

  const assignableUsers = useAssignableUsers();
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of assignableUsers.data ?? []) m.set(u.id, u.displayName);
    return m;
  }, [assignableUsers.data]);

  const totalPages = useMemo(() => {
    const data = list.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [list.data]);

  const columns: Column<PrequalificationSummaryDto>[] = useMemo(() => [
    {
      key: "name",
      header: "Name",
      render: (s) => (
        <Link to={`/deal-flow/prequalification/${s.id}`} className="font-semibold hover:text-gold-shadow">
          {s.firstName} {s.lastName}
          <div className="text-caption text-gray-mid font-normal">{s.email}</div>
          {s.referenceNumber && (
            <div className="text-caption text-gray-mid font-normal font-mono">
              {s.referenceNumber}
            </div>
          )}
        </Link>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (s) => <Badge status={s.role === "Buyer" ? "info" : "gold"}>{s.role}</Badge>,
    },
    {
      key: "site",
      header: "Site",
      // Same reading as the invitations table: a null slug is the Phoenix-native
      // funnel, not a missing value. Without this column the new native filter
      // would narrow the table with nothing on screen to explain what it did.
      render: (s) => <span className="text-caption">{s.partnerSlug ?? "Phoenix"}</span>,
    },
    {
      key: "industry",
      header: "Industry / Business",
      render: (s) => s.industryOrBusiness
        ? <span className="text-caption">{s.industryOrBusiness}</span>
        : <span className="text-caption text-gray-mid italic">—</span>,
    },
    {
      key: "amount",
      header: "Qualified / Financed",
      render: (s) => <span className="font-mono text-caption">{amountCell(s)}</span>,
    },
    {
      key: "result",
      header: "Result",
      render: (s) => <Badge status={PREQUAL_OUTCOME_BADGE[s.outcome]}>{outcomeLabel(s.outcome)}</Badge>,
    },
    {
      key: "relationship",
      header: "Relationship",
      // Rendered UNCONDITIONALLY, dash and all: a column that appears when the
      // page happens to contain an internal buyer and vanishes when you page
      // past them is worse than a column of dashes, and an operator filtering
      // on verification needs the answer to be where it always is. The dash is
      // "no relationship was asserted", which for a seller or a capital-sized
      // buyer is the whole truth.
      render: (s) => {
        const badge = internalVerificationBadge(s);
        return badge
          ? <Badge status={badge.status}>{badge.label}</Badge>
          : <span className="text-caption text-gray-mid italic">—</span>;
      },
    },
    {
      key: "inBuyerCrm",
      header: "In Buyer CRM",
      // A dash for a seller: the question does not apply, and "No" would read
      // as a seller who was missed.
      render: (s) =>
        s.role !== "Buyer" || s.inBuyerCrm === undefined
          ? <span className="text-caption text-gray-mid italic" data-testid="prequal-in-buyer-crm">—</span>
          : <span className="text-small" data-testid="prequal-in-buyer-crm">{s.inBuyerCrm ? "Yes" : "No"}</span>,
    },
    {
      key: "docs",
      header: "Documents",
      render: (s) => (
        // The testid exists so the three assertions about this cell's dash can
        // be scoped to it. They were scoped to the whole TABLE, which held for
        // exactly as long as no other column rendered a dash — and the
        // Relationship column beside it now does, for every submission that is
        // not an internal buyer. An assertion about one cell should name it.
        <div className="flex gap-space-1" data-testid="prequal-documents-cell">
          {s.hasLetter && <Badge status="draft">Letter</Badge>}
          {s.hasDealSheet && <Badge status="draft">Deal sheet</Badge>}
          {/* The private analysis IS produced — this column under-reported it
              while only two of the three were rendered. */}
          {s.hasAnalysis && <Badge status="draft">Analysis</Badge>}
          {/* 🔴 Says WHY there is no deal sheet, rather than leaving a reader to
              guess. A letter-only buyer asked for exactly what they got, and a
              column that shows only what exists cannot tell that apart from a
              document that failed to generate — which is the reading this whole
              row is here to prevent. Muted prose, not a pill: it is the absence
              of a document, not a state of one. */}
          {s.buyerDocumentPath === "LetterOnly" && (
            <span className="text-caption text-gray-mid">Letter only, by request</span>
          )}
          {/* 🔴 EXCLUSIVE OF THE ROW ABOVE, and that is the whole point. These
              two clauses were independent, so a letter-only buyer whose LETTER
              failed rendered both in one flex row — "Letter only, by request —" —
              which reads as "the absence is by request" and is exactly the
              misreading the note above exists to prevent. The server-side filter
              correctly flags that submission as an incident, so the two surfaces
              disagreed about the one row where it matters.

              Keyed on LetterOnly rather than on "a package was recorded": a
              LetterAndDealSheet buyer holding nothing gets NO explanatory note,
              so the dash is the only thing that would say anything at all, and
              suppressing it there would trade one silent row for another. */}
          {s.buyerDocumentPath !== "LetterOnly"
            && !s.hasLetter && !s.hasDealSheet && !s.hasAnalysis && (
            <span className="text-caption text-gray-mid">—</span>
          )}
        </div>
      ),
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (s) => <span className="text-caption">{date(s.createdAt)}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (s) => <Badge status={PREQUAL_STATUS_BADGE[s.status]}>{s.status}</Badge>,
    },
    {
      key: "assigned",
      header: "Assigned",
      render: (s) => (
        <span className="text-caption text-gray-mid">
          {s.assignedToUserId
            ? userNameById.get(s.assignedToUserId) ?? s.assignedToUserId.slice(0, 8)
            : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-10 text-right",
      render: (s) => (
        <Link to={`/deal-flow/prequalification/${s.id}`} aria-label="Open submission">
          <Button variant="tertiary" size="sm"><Eye size={14} strokeWidth={1.5} aria-hidden /></Button>
        </Link>
      ),
    },
  ], [date, userNameById]);

  return (
    // See DraftsSection — the selected tab above carries this section's
    // heading, and the tabpanel owns the gap beneath the strip.
    <section aria-label="Completions">
      <StatsBar stats={stats} />

      <div className="flex flex-wrap items-center gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="grow min-w-64"
        />
        <select
          className="h-10 w-36 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={role}
          onChange={(e) => setRole(e.target.value as PrequalificationRole | "")}
          aria-label="Role filter"
        >
          <option value="">All roles</option>
          <option value="Buyer">Buyer</option>
          <option value="Seller">Seller</option>
        </select>
        <select
          className="h-10 w-40 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={status}
          onChange={(e) => setStatus(e.target.value as PrequalificationStatus | "" | "Archived")}
          aria-label="Status filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          <option value="Archived">Archived</option>
        </select>
        <select
          className="h-10 w-40 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as PrequalificationOutcome | "")}
          aria-label="Result filter"
        >
          <option value="">All results</option>
          <option value="Pass">Pass</option>
          <option value="SoftDecline">Soft decline</option>
        </select>
        <select
          className="h-10 w-48 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={internalVerification}
          onChange={(e) =>
            setInternalVerification(e.target.value as PrequalInternalVerification | "")
          }
          aria-label="Relationship verification filter"
        >
          <option value="">All relationships</option>
          {/* The group label is load-bearing, not decoration: the server ANDs
              the internal path onto this filter, so every option below also
              hides every seller. Saying so here is what stops that reading as
              a bug. */}
          <optgroup label="Internal buyers">
            <option value="Unverified">Unchecked</option>
            <option value="Verified">Verified</option>
            <option value="NotVerified">Not verified</option>
          </optgroup>
        </select>
        <select
          className="h-10 w-48 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={inBuyerCrm}
          onChange={(e) => setInBuyerCrm(e.target.value as "" | "yes" | "no")}
          aria-label="In Buyer CRM filter"
        >
          <option value="">In Buyer CRM: any</option>
          <option value="yes">In Buyer CRM: yes</option>
          {/* "No" also hides every seller — the server scopes it to buyers. */}
          <option value="no">In Buyer CRM: no (buyers)</option>
        </select>
        {canReadBrands && brandOptions.length > 0 && (
          <select
            className="h-10 w-48 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            aria-label="Partner site filter"
          >
            <option value="">All sites</option>
            {/* Native submissions exist and had no way to be selected: "" is
                already "no filter", so this rides its own server flag. */}
            <option value={NATIVE_SITE}>Phoenix (native)</option>
            {brandOptions.map((b) => (
              <option key={b.id} value={b.id}>{b.displayName}</option>
            ))}
          </select>
        )}
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={documentsMissing}
            onChange={(e) => setDocumentsMissing(e.target.checked)}
          />
          <span>Missing documents</span>
        </label>
      </div>

      {/* Said in place rather than in a tooltip, because what an operator needs
          is the RULE, not the count: the expected set differs by outcome, so a row
          that looks complete for one outcome is incomplete for the other.

          This caption used to read "Soft declines are excluded — they produce no
          documents by design", which was true until declines got a document set of
          their own. It is the third place that claim was written down (the service
          predicate and its test being the other two) and the only one a person
          reads, so it is the one that would have quietly taught an operator the
          wrong rule. */}
      {documentsMissing && (
        <p className="text-caption text-gray-mid mb-space-3">
          Showing submissions missing a document they should have. A Pass is
          measured on its letter and deal sheet; a soft decline on its letter and
          analysis — a decline never has a deal sheet, and is not flagged for it.
          For a Pass the private analysis is not part of the test, because it is
          switched on per site and its absence is normal; for a decline it is,
          because it is always generated. Declines recorded before that set
          existed will appear here — narrow by date to separate them.
        </p>
      )}

      <DataTable
        columns={columns}
        rows={list.data?.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No prequalification submissions match your filters."
        rowKey={(s) => s.id}
      />

      {list.data && list.data.totalCount > 0 && (
        <Pagination
          page={list.data.page}
          pageSize={list.data.pageSize}
          totalCount={list.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </section>
  );
}

/**
 * Applications that were started and never finished.
 *
 * A PARALLEL section over a parallel source, deliberately — the completions
 * tab is rooted on submissions, and a draft is neither a submission nor an
 * invite, so no filter on that table could ever surface one. The invitations
 * section solved the same shape the same way.
 *
 * The default view is "Abandoned", because that is the actionable half: a draft
 * somebody touched an hour ago needs nobody's attention, and a follow-up queue
 * that opens on everything buries the handful of rows worth a phone call.
 */
function DraftsSection({ canAssist }: { canAssist: boolean }) {
  const { date, relative } = useDateFormatter();
  const [scope, setScope] = useState<PrequalificationDraftScope>("Abandoned");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [scope]);

  const drafts = usePrequalificationDrafts({ page, pageSize: PAGE_SIZE, scope });

  const totalPages = useMemo(() => {
    const data = drafts.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [drafts.data]);

  const columns: Column<PrequalificationDraftSummaryDto>[] = useMemo(() => [
    {
      key: "applicant",
      header: "Applicant",
      render: (d) => (
        <div>
          <div className="font-semibold">{d.firstName} {d.lastName}</div>
          <div className="text-caption text-gray-mid">{d.email}</div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (d) => <Badge status={d.role === "Buyer" ? "info" : "gold"}>{d.role}</Badge>,
    },
    {
      key: "progress",
      header: "Progress",
      // `stepIndex` is a ZERO-BASED position, so rendering it raw reported "0 of
      // 6 steps" for everybody on their first screen — on the queue staff triage
      // follow-ups from. +1 turns the position into a human count; the
      // `Math.min` holds the ceiling for a row stored past the end of its own
      // funnel (see `ClampStep` — seller rows written under the old bound are
      // not normalised by any migration). Same expression as the portal's
      // `DraftsPanel.stepLabel`, which had it right first.
      render: (d) => (
        <span className="text-caption">
          {Math.min(d.stepIndex + 1, d.stepCount)} of {d.stepCount} steps
        </span>
      ),
    },
    {
      key: "source",
      header: "Source",
      // An ORGANIC draft has no invite and therefore no invite status. It must
      // never fall through to the invite-status badge: reporting that Phoenix
      // invited a stranger who wandered onto the public funnel would be actively
      // wrong, and these are exactly the rows this section exists to surface.
      render: (d) => (d.inviteStatus
        ? <Badge status={PREQUAL_INVITE_STATUS_BADGE[d.inviteStatus]}>{d.inviteStatus}</Badge>
        : <span className="text-caption text-gray-mid italic">Not invited</span>),
    },
    {
      key: "activity",
      header: "Last activity",
      render: (d) => (
        <div className="text-caption">
          {relative(d.lastActivityAtUtc)}
          <div className="text-gray-mid">Started {date(d.createdAt)}</div>
        </div>
      ),
    },
    {
      key: "state",
      header: "State",
      render: (d) => (d.isAbandoned
        ? <Badge status="review">Abandoned</Badge>
        : <Badge status="draft">In progress</Badge>),
    },
    // Picking one up is the whole point of this queue: a stale row is worth a
    // phone call, and this is what happens next once somebody picks up.
    // Rendered only for callers who may actually drive the form, so nobody is
    // offered an action that 403s.
    ...(canAssist ? [{
      key: "assist",
      header: "",
      render: (d: PrequalificationDraftSummaryDto) => (
        // The accessible name sits on the LINK, not on the button inside it:
        // that is the element a keyboard or screen-reader user activates, and
        // one row's "Continue" is otherwise indistinguishable from the next.
        <Link
          to={`/deal-flow/prequalify/assist/${d.draftId}`}
          aria-label={`Continue ${d.firstName} ${d.lastName}'s application`}
        >
          <Button variant="tertiary" size="sm" tabIndex={-1}>
            <PenLine size={14} strokeWidth={1.5} aria-hidden className="mr-space-2" />
            Continue
          </Button>
        </Link>
      ),
    }] : []),
  ], [date, relative, canAssist]);

  return (
    // No heading of its own: the selected tab above IS this section's label,
    // and repeating it would read as two nested titles. The aria-label keeps
    // the region addressable, and stays the fuller wording — the tab strip has
    // three labels to fit, a landmark list does not. No margin of its own
    // either: the tabpanel owns the gap under the strip for all three panels.
    <section aria-label="In-progress applications">
      <div className="flex flex-wrap items-center gap-space-3 mb-space-3">
        <div
          role="group"
          aria-label="Draft scope"
          className="inline-flex shrink-0 rounded-md border border-border-light bg-white-pure overflow-hidden"
        >
          <button
            type="button"
            onClick={() => setScope("Abandoned")}
            aria-pressed={scope === "Abandoned"}
            className={cn(
              "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
              scope === "Abandoned" ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
            )}
          >
            Abandoned
          </button>
          <button
            type="button"
            onClick={() => setScope("All")}
            aria-pressed={scope === "All"}
            className={cn(
              "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
              scope === "All" ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
            )}
          >
            {/* "All drafts", not "All": the invitations tab has an "All"
                toggle of its own. The two no longer co-mount, but a name that
                does not depend on which tab you landed on is still the one a
                screen reader — or a test — can act on unambiguously. */}
            All drafts
          </button>
        </div>
        {drafts.data && (
          <span className="text-caption text-gray-mid">
            {drafts.data.totalCount}{" "}
            {scope === "Abandoned" ? "abandoned" : "in progress"}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={drafts.data?.data ?? []}
        loading={drafts.isLoading}
        error={drafts.isError ? (drafts.error as Error).message : null}
        emptyMessage={scope === "Abandoned"
          ? "No applications have gone stale."
          : "No applications are in progress."}
        rowKey={(d) => d.draftId}
      />

      {drafts.data && drafts.data.totalCount > 0 && (
        <Pagination
          page={drafts.data.page}
          pageSize={drafts.data.pageSize}
          totalCount={drafts.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </section>
  );
}

/**
 * The outreach list: who has been invited and who we are still waiting on,
 * alongside the ones that converted. Scoped to the signed-in
 * sender by default — an invite is a personal follow-up, so "mine" is the
 * view you want first; "All" is one click away for cover and support.
 */
function InvitesSection({ canManage }: { canManage: boolean }) {
  const { date } = useDateFormatter();
  const [mineOnly, setMineOnly] = useState(true);
  const [status, setStatus] = useState<PrequalInviteStatus | "" | "Outstanding">("");
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [mineOnly, status]);

  const invites = usePrequalificationInvites({
    page,
    pageSize: PAGE_SIZE,
    mineOnly: mineOnly || undefined,
    // "Outstanding" is its own server flag, not a status value.
    outstanding: status === "Outstanding" || undefined,
    status: status === "" || status === "Outstanding" ? undefined : status,
  });

  const resend = useResendPrequalificationInvite();
  const revoke = useRevokePrequalificationInvite();
  const busy = resend.isPending || revoke.isPending;

  const totalPages = useMemo(() => {
    const data = invites.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [invites.data]);

  const columns: Column<PrequalInviteDto>[] = useMemo(() => [
    {
      key: "recipient",
      header: "Recipient",
      render: (i) => (
        <div>
          <div className="font-semibold">{i.firstName} {i.lastName}</div>
          <div className="text-caption text-gray-mid">{i.email}</div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (i) => <Badge status={i.role === "Buyer" ? "info" : "gold"}>{i.role}</Badge>,
    },
    {
      key: "site",
      header: "Site",
      render: (i) => <span className="text-caption">{i.partnerSlug ?? "Phoenix"}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (i) => (
        <Badge status={PREQUAL_INVITE_STATUS_BADGE[i.status]}>{i.status}</Badge>
      ),
    },
    {
      key: "sent",
      header: "Sent",
      render: (i) => (
        <div className="text-caption">
          {date(i.sentAtUtc)}
          {i.sendCount > 1 && (
            <div className="text-gray-mid">Resent &times;{i.sendCount - 1}</div>
          )}
        </div>
      ),
    },
    {
      key: "expires",
      header: "Expires",
      render: (i) => <span className="text-caption">{date(i.expiresAtUtc)}</span>,
    },
    {
      key: "sender",
      header: "Sender",
      render: (i) => (
        <span className="text-caption text-gray-mid">{i.invitedByLabel ?? "—"}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (i) => (
        <div className="flex justify-end gap-space-1">
          <Button
            variant="tertiary"
            size="sm"
            aria-label={`Copy invitation link for ${i.email}`}
            onClick={() => {
              void navigator.clipboard?.writeText(i.inviteUrl);
              setCopiedId(i.id);
            }}
          >
            <Copy size={14} strokeWidth={1.5} aria-hidden />
            {copiedId === i.id && <span className="ml-space-1">Copied</span>}
          </Button>
          {canManage && (
            <>
              {/* Disabled, not hidden — a control that vanishes leaves the
                  reader wondering whether it was ever there. The reason rides
                  the native `title` for now; WS7: replace with the shared
                  Tooltip primitive at
                  design-system/components/ui/Tooltip.tsx. */}
              <Button
                variant="tertiary"
                size="sm"
                aria-label={`Resend invitation to ${i.email}`}
                title={terminalInviteReason(i.status) ?? undefined}
                disabled={busy || isTerminalInviteStatus(i.status)}
                onClick={() => resend.mutate(i.id)}
              >
                <RotateCw size={14} strokeWidth={1.5} aria-hidden />
              </Button>
              <Button
                variant="tertiary"
                size="sm"
                aria-label={`Revoke invitation to ${i.email}`}
                title={terminalInviteReason(i.status) ?? undefined}
                disabled={busy || isTerminalInviteStatus(i.status)}
                onClick={() => revoke.mutate(i.id)}
              >
                <Ban size={14} strokeWidth={1.5} aria-hidden />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ], [canManage, copiedId, busy, date, resend, revoke]);

  return (
    // See DraftsSection — the tab above carries the heading, the tabpanel the
    // spacing.
    <section aria-label="Invitations">
      <div className="flex flex-wrap items-center gap-space-3 mb-space-3">
        <div
          role="group"
          aria-label="Invitation scope"
          className="inline-flex shrink-0 rounded-md border border-border-light bg-white-pure overflow-hidden"
        >
          <button
            type="button"
            onClick={() => setMineOnly(true)}
            aria-pressed={mineOnly}
            className={cn(
              "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
              mineOnly ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
            )}
          >
            My invitations
          </button>
          <button
            type="button"
            onClick={() => setMineOnly(false)}
            aria-pressed={!mineOnly}
            className={cn(
              "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
              !mineOnly ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
            )}
          >
            All
          </button>
        </div>
        <select
          className="h-10 w-44 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as PrequalInviteStatus | "" | "Outstanding")
          }
          aria-label="Invitation status filter"
        >
          <option value="">All invitations</option>
          {/* "Outstanding" is a pseudo-option, not a status: it maps to the
              server's own `outstanding` flag (see the query above). */}
          <option value="Outstanding">Outstanding</option>
          {PREQUAL_INVITE_FILTER_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={invites.data?.data ?? []}
        loading={invites.isLoading}
        error={invites.isError ? (invites.error as Error).message : null}
        emptyMessage="No invitations match your filters."
        rowKey={(i) => i.id}
      />

      {invites.data && invites.data.totalCount > 0 && (
        <Pagination
          page={invites.data.page}
          pageSize={invites.data.pageSize}
          totalCount={invites.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </section>
  );
}

/**
 * Two DIFFERENT axes over the same submissions, plus their shared total.
 *
 * Status (New / Reviewed / Converted / Disqualified) and outcome (Pass / Soft
 * decline) each partition the whole set, so every submission is counted once
 * under each. Laid out as one flat 7-across strip they read as a single series
 * that double-counts. The arithmetic was always right — this only groups and
 * divides the axes so it is obvious they are measured differently. No count
 * and no query changed.
 */
function StatsBar({ stats }: { stats: PrequalificationStatsDto }) {
  return (
    <div className="flex flex-wrap items-end gap-space-4 mb-space-4">
      <StatCard label="Total" value={stats.total} />
      <StatGroup label="By status">
        <StatCard label="New" value={stats.byStatus.New} />
        <StatCard label="Reviewed" value={stats.byStatus.Reviewed} />
        <StatCard label="Converted" value={stats.byStatus.Converted} />
        <StatCard label="Disqualified" value={stats.byStatus.Disqualified} />
      </StatGroup>
      <StatGroup label="By result">
        <StatCard label="Pass" value={stats.passCount} />
        <StatCard label="Soft decline" value={stats.softDeclineCount} />
      </StatGroup>
    </div>
  );
}

/** One axis of the counters, captioned and ruled off from its neighbour. */
function StatGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pl-0 border-l-0 lg:pl-space-4 lg:border-l lg:border-border-light">
      <div className="text-label uppercase tracking-wide text-gray-mid mb-space-1">
        {label}
      </div>
      <div className="flex flex-wrap gap-space-3">{children}</div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-36 grow border border-border-light rounded-md p-space-3 bg-white-pure">
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="font-display text-h3 text-onyx">{value}</div>
    </div>
  );
}
